import React, { useState, useEffect } from 'react';
import {
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import { OddsToggle } from './OddsToggle';
import { Close } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getDriverMatchups, getDriverTrackRecentRaces, DriverMatchup, DriverTrackRecentRace } from '../services/api';
import { maxScreenHeight, stickyModalHeader } from '../utils/viewport';

// Color constants - exactly matching loop data table
const COLORS = {
  max: "#62BF7B", // Green
  mid: "#FDE883", // Yellow
  min: "#F9696D", // Red
};

// Helper function to interpolate between two colors
const interpolateColor = (color1: string, color2: string, factor: number) => {
  const result = color1.match(/\w\w/g)?.map((c, i) => {
    const c1 = parseInt(c, 16);
    const c2 = parseInt(color2.match(/\w\w/g)![i], 16);
    const value = Math.round(c1 + factor * (c2 - c1));
    return value.toString(16).padStart(2, '0');
  });
  return `#${result?.join('')}`;
};

// Color mapping for Last 10 Races - same as loopdata table
const recentRaceColorMapping: Record<string, { type: 'position' | 'probability' | 'rating'; invert?: boolean }> = {
  St: { type: 'position' },
  Pos: { type: 'position' },
  Avg: { type: 'position' },
  DriverRating: { type: 'rating' },
  PctT15Lap: { type: 'probability' },
};

function getRecentRaceCellColor(value: number, field: string): string | undefined {
  const mapping = recentRaceColorMapping[field];
  if (!mapping) return undefined;

  let normalizedValue: number;
  switch (mapping.type) {
    case 'position':
      // For positions: 1 (green) to 40 (red)
      normalizedValue = Math.min(Math.max(value, 1), 40);
      normalizedValue = mapping.invert ? (normalizedValue - 1) / 39 : 1 - (normalizedValue - 1) / 39;
      break;
    case 'probability':
      // For probabilities: 0 (red) to 1 (green) - PctT15Lap is already 0-1
      normalizedValue = Math.min(Math.max(value, 0), 1);
      break;
    case 'rating':
      const min = 0;
      const max = value > 100 ? value * 1.1 : 100;
      normalizedValue = Math.max(0, Math.min(1, (value - min) / (max - min)));
      normalizedValue = mapping.invert ? 1 - normalizedValue : normalizedValue;
      break;
    default:
      return undefined;
  }

  if (normalizedValue <= 0.5) {
    return interpolateColor(COLORS.min, COLORS.mid, normalizedValue * 2);
  } else {
    return interpolateColor(COLORS.mid, COLORS.max, (normalizedValue - 0.5) * 2);
  }
}

// Utility function to convert probability to American odds
const toAmericanOdds = (probability: number): string => {
  if (probability <= 0) return "N/A";
  const prob = probability * 100;
  let odds: number;
  
  if (prob < 50) {
    odds = (100 / prob) * 100 - 100;
    return `+${Math.round(odds)}`;
  } else {
    odds = -(prob / (100 - prob)) * 100;
    return Math.round(odds).toString();
  }
};



interface MatchupsModalProps {
  open: boolean;
  onClose: () => void;
  track: string;
  driver: string;
}

// Column config for Matchups tab
const MATCHUPS_COLUMNS = [
  { id: 'opponent', label: 'Opponent', align: 'left' as const },
  { id: 'winProb', label: 'Win Probability', align: 'right' as const, width: '150px' },
];

// Column config for Last 10 Races tab
const RECENT_RACES_COLUMNS = [
  { id: 'Date', label: 'Date', align: 'left' as const, field: 'Date' as keyof DriverTrackRecentRace },
  { id: 'St', label: 'Start', align: 'right' as const, field: 'St' as keyof DriverTrackRecentRace },
  { id: 'Pos', label: 'Finish', align: 'right' as const, field: 'Pos' as keyof DriverTrackRecentRace },
  { id: 'Avg', label: 'Avg', align: 'right' as const, field: 'Avg' as keyof DriverTrackRecentRace },
  { id: 'DriverRating', label: 'Driver Rating', align: 'right' as const, field: 'DriverRating' as keyof DriverTrackRecentRace },
  { id: 'PctT15Lap', label: '% T15', align: 'right' as const, field: 'PctT15Lap' as keyof DriverTrackRecentRace },
];

const SortableHeaderCell: React.FC<{
  id: string;
  label: string;
  align: 'left' | 'right';
  width?: string;
  dynamicLabel?: string;
}> = ({ id, label, align, width, dynamicLabel }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <TableCell
      ref={setNodeRef}
      align={align}
      sx={{
        fontWeight: 'bold',
        cursor: 'grab',
        whiteSpace: 'nowrap',
        width: width ?? undefined,
        minWidth: width ?? undefined,
        '&:hover': { backgroundColor: 'action.hover' },
      }}
      style={style}
      {...attributes}
      {...listeners}
    >
      {dynamicLabel ?? label}
    </TableCell>
  );
};

const SortableAverageBox: React.FC<{
  id: string;
  label: string;
  value: string;
  bgColor: string | undefined;
}> = ({ id, label, value, bgColor }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <Box
      ref={setNodeRef}
      sx={{
        backgroundColor: bgColor,
        color: bgColor ? '#000' : 'text.primary',
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        cursor: 'grab',
        minWidth: 80,
        '&:active': { cursor: 'grabbing' },
        '&:hover': { boxShadow: 1 },
      }}
      style={style}
      {...attributes}
      {...listeners}
    >
      <Typography variant="caption" sx={{ color: bgColor ? 'rgba(0,0,0,0.7)' : 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight="medium">
        {value}
      </Typography>
    </Box>
  );
};

export const MatchupsModal: React.FC<MatchupsModalProps> = ({ open, onClose, track, driver }) => {
  const [matchups, setMatchups] = useState<DriverMatchup[]>([]);
  const [recentRaces, setRecentRaces] = useState<DriverTrackRecentRace[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [matchupsColumnOrder, setMatchupsColumnOrder] = useState<string[]>(MATCHUPS_COLUMNS.map((c) => c.id));
  const [recentRacesColumnOrder, setRecentRacesColumnOrder] = useState<string[]>(RECENT_RACES_COLUMNS.map((c) => c.id));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [recentRacesLoading, setRecentRacesLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [recentRacesError, setRecentRacesError] = useState<string | null>(null);
  const [showAmericanOdds, setShowAmericanOdds] = useState<boolean>(false);

  const sensors = useSensors(useSensor(PointerSensor));

  const matchupsColumnsOrdered = matchupsColumnOrder
    .map((id) => MATCHUPS_COLUMNS.find((c) => c.id === id))
    .filter(Boolean) as typeof MATCHUPS_COLUMNS;
  const recentRacesColumnsOrdered = recentRacesColumnOrder
    .map((id) => RECENT_RACES_COLUMNS.find((c) => c.id === id))
    .filter(Boolean) as typeof RECENT_RACES_COLUMNS;

  const handleMatchupsDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setMatchupsColumnOrder((prev) => {
        const oldIndex = prev.indexOf(active.id as string);
        const newIndex = prev.indexOf(over.id as string);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleRecentRacesDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setRecentRacesColumnOrder((prev) => {
        const oldIndex = prev.indexOf(active.id as string);
        const newIndex = prev.indexOf(over.id as string);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  useEffect(() => {
    if (open && track && driver) {
      setTabValue(0);
      setIsLoading(true);
      setError(null);
      getDriverMatchups({ track, driver })
        .then((results) => {
          const sortedResults = [...results].sort((a, b) => b.prob_a_wins - a.prob_a_wins);
          setMatchups(sortedResults);
        })
        .catch((err) => {
          setError('Failed to fetch matchups');
          console.error('Error fetching matchups:', err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [open, track, driver]);

  useEffect(() => {
    if (open && track && driver) {
      setRecentRacesLoading(true);
      setRecentRacesError(null);
      getDriverTrackRecentRaces({ track, driver, limit: 10 })
        .then(setRecentRaces)
        .catch((err) => {
          setRecentRacesError('Failed to fetch recent races');
          console.error('Error fetching recent races:', err);
        })
        .finally(() => {
          setRecentRacesLoading(false);
        });
    }
  }, [open, track, driver]);

  const formatProbability = (prob: number): string => {
    return `${(prob * 100).toFixed(1)}%`;
  };

  // Averages for last 10 races
  const recentAverages = recentRaces.length > 0
    ? {
        St: recentRaces.reduce((s, r) => s + (r.St ?? 0), 0) / recentRaces.length,
        Pos: recentRaces.reduce((s, r) => s + (r.Pos ?? 0), 0) / recentRaces.length,
        Avg: recentRaces.reduce((s, r) => s + (r.Avg ?? 0), 0) / recentRaces.length,
        DriverRating: recentRaces.reduce((s, r) => s + (r.DriverRating ?? 0), 0) / recentRaces.length,
        PctT15Lap: recentRaces.reduce((s, r) => s + (r.PctT15Lap ?? 0), 0) / recentRaces.length,
      }
    : null;

  // Get cell color for win probability using the same gradient as loopdata table
  const getCellColor = (prob: number) => {
    // For probabilities: 0 (red) to 1 (green)
    const normalizedValue = Math.min(Math.max(prob, 0), 1);
    
    if (normalizedValue <= 0.5) {
      return interpolateColor(COLORS.min, COLORS.mid, normalizedValue * 2);
    } else {
      return interpolateColor(COLORS.mid, COLORS.max, (normalizedValue - 0.5) * 2);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper
        sx={{
          width: '90%',
          maxWidth: '800px',
          ...maxScreenHeight(90),
          overflow: 'auto',
          p: 3,
          pt: 0,
        }}
      >
        <Box sx={{ ...stickyModalHeader, pt: 3, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {driver} at {track}
          </Typography>
          <IconButton onClick={onClose} aria-label="Close">
            <Close />
          </IconButton>
        </Box>

        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
          <Tab label="Matchups" />
          <Tab label="Last 10 Races" />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <OddsToggle checked={showAmericanOdds} onChange={setShowAmericanOdds} />
          </Box>
        )}

        {tabValue === 0 && (
          <>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Typography color="error">{error}</Typography>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleMatchupsDragEnd}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <SortableContext
                          items={matchupsColumnOrder}
                          strategy={horizontalListSortingStrategy}
                        >
                          {matchupsColumnsOrdered.map((col) => (
                            <SortableHeaderCell
                              key={col.id}
                              id={col.id}
                              label={col.label}
                              align={col.align}
                              width={col.width}
                              dynamicLabel={col.id === 'winProb' ? (showAmericanOdds ? 'Win Odds' : 'Win Probability') : undefined}
                            />
                          ))}
                        </SortableContext>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matchups.map((matchup) => (
                        <TableRow key={matchup.driver_b} hover>
                          {matchupsColumnsOrdered.map((col) => {
                            if (col.id === 'opponent') {
                              return (
                                <TableCell key={col.id} align={col.align}>
                                  {matchup.driver_b}
                                </TableCell>
                              );
                            }
                            return (
                              <TableCell
                                key={col.id}
                                align="right"
                                sx={{
                                  backgroundColor: getCellColor(matchup.prob_a_wins),
                                  color: '#000000',
                                  fontWeight: 'medium',
                                  transition: 'all 0.2s',
                                  width: col.width ?? undefined,
                                  minWidth: col.width ?? undefined,
                                }}
                              >
                                {showAmericanOdds
                                  ? toAmericanOdds(matchup.prob_a_wins)
                                  : formatProbability(matchup.prob_a_wins)}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </DndContext>
            )}
          </>
        )}

        {tabValue === 1 && (
          <>
            {recentRacesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : recentRacesError ? (
              <Typography color="error">{recentRacesError}</Typography>
            ) : (
              <>
                {recentAverages && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Averages (last {recentRaces.length} races)
                    </Typography>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRecentRacesDragEnd}>
                      <SortableContext
                        items={recentRacesColumnsOrdered.filter((c) => c.field !== 'Date').map((c) => c.id)}
                        strategy={horizontalListSortingStrategy}
                      >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                          {recentRacesColumnsOrdered
                            .filter((col) => col.field !== 'Date')
                            .map((col) => {
                              const value = recentAverages[col.field as keyof typeof recentAverages];
                              const numValue = typeof value === 'number' ? value : null;
                              const isPercent = col.field === 'PctT15Lap';
                              const bgColor = numValue != null ? getRecentRaceCellColor(numValue, col.id) : undefined;
                              const displayLabel =
                                col.id === 'St' ? 'Start' : col.id === 'Pos' ? 'Finish (Pos)' : col.id === 'Avg' ? 'Avg Running Pos' : col.id === 'DriverRating' ? 'Driver Rating' : '% Top 15';
                              const displayValue =
                                isPercent && numValue != null ? `${(numValue * 100).toFixed(1)}%` : numValue != null ? numValue.toFixed(1) : '-';
                              return (
                                <SortableAverageBox
                                  key={col.id}
                                  id={col.id}
                                  label={displayLabel}
                                  value={displayValue}
                                  bgColor={bgColor}
                                />
                              );
                            })}
                        </Box>
                      </SortableContext>
                    </DndContext>
                  </Paper>
                )}
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRecentRacesDragEnd}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <SortableContext
                            items={recentRacesColumnOrder}
                            strategy={horizontalListSortingStrategy}
                          >
                            {recentRacesColumnsOrdered.map((col) => (
                              <SortableHeaderCell
                                key={col.id}
                                id={col.id}
                                label={col.label}
                                align={col.align}
                              />
                            ))}
                          </SortableContext>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentRaces.map((race) => (
                          <TableRow key={race.Date} hover>
                            {recentRacesColumnsOrdered.map((col) => {
                              const value = race[col.field];
                              const numValue = typeof value === 'number' ? value : null;
                              const isPercent = col.field === 'PctT15Lap';
                              const isDate = col.field === 'Date';
                              const bgColor = numValue != null && !isDate ? getRecentRaceCellColor(numValue, col.id) : undefined;
                              return (
                                <TableCell
                                  key={col.id}
                                  align={col.align}
                                  sx={{
                                    backgroundColor: bgColor,
                                    color: bgColor ? '#000' : 'text.primary',
                                    fontWeight: 'medium',
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  {isDate
                                    ? new Date(value as string).toLocaleDateString()
                                    : isPercent
                                    ? numValue != null ? `${(numValue * 100).toFixed(1)}%` : '-'
                                    : numValue != null ? numValue.toFixed(1) : '-'}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </DndContext>
              </>
            )}
          </>
        )}
      </Paper>
    </Modal>
  );
};
